import React, { FunctionComponent } from 'react';
import { Translate } from 'react-localize-redux';
import DropdownCtrl from '@webapp/controllers/form/dropdown';
import DropdownItemCheck from '@components/form/dropdown/dropdown-item-check';

import {
  TracPageFeatureItem,
  TracPageFeatureItemType,
} from '@services/tracpage/types/trac-page-feature-item';
import styled from 'styled-components';
import { UpdateTracPageField } from '@webapp/store/tracpage/action-creators';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import { Dispatch } from 'redux';
import { CloseMenu } from '@webapp/store/ui/action-creators';
import { TracPageLink } from '@services/tracpage/types/trac-page-link';
import TracPage from '@services/tracpage/types/trac-page';
import Typography from '@components/basics/typography';

type FeaturedItem = {
  id?: string;
  value?: string;
  imageUrl?: string;
};

type FeaturedSection = {
  value: TracPageFeatureItemType;
  label: React.ReactNode | string;
  items: FeaturedItem[];
};

const musicReleases = {
  value: TracPageFeatureItemType.MusicRelease,
  label: (
    <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.FEATURED.DROPDOWN.MUSIC" />
  ),
  items: [],
};

const merchProducts = {
  value: TracPageFeatureItemType.MerchProduct,
  label: (
    <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.FEATURED.DROPDOWN.MERCH" />
  ),
  items: [],
};

const trackLinks = {
  value: TracPageFeatureItemType.TracLink,
  label: (
    <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.FEATURED.DROPDOWN.LINKS" />
  ),
  items: [],
};

interface FeaturedItemContainerProps {
  links: TracPageLink[];
  releases: Record<'id' | 'value' | 'imageUrl', string | undefined>[];
  merchList: Record<'id' | 'value' | 'imageUrl', string | undefined>[];
  updateFeaturedItem: (
    featuredItem: TracPageFeatureItem
  ) => UpdateTracPageField<TracPage, 'featuredItem'>;
  featuredItem?: TracPageFeatureItem;
}

const mapStateToProps = (state: AppState) => {
  return {
    featuredItem: state.tracpage.artistPage.featuredItem,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
  updateFeaturedItem: (featuredItem: TracPageFeatureItem) =>
    dispatch(
      new UpdateTracPageField<TracPage, 'featuredItem'>(
        'tracpage',
        'featuredItem',
        featuredItem
      )
    ),
});

const FeaturedItemContainer: FunctionComponent<FeaturedItemContainerProps> = ({
  releases,
  merchList,
  links,
  updateFeaturedItem,
  featuredItem,
}) => {
  const [featured, setFeatured] = React.useState('');
  const [customFeaturedFilterValue, setCustomFeaturedFilterValue] =
    React.useState<string>('');

  React.useEffect(() => {
    if (!featuredItem || !TracPageFeatureItemType[featuredItem.type]) {
      return setFeatured('');
    }

    if (featuredItem.type === TracPageFeatureItemType.MusicRelease) {
      return setFeatured(
        releases.find((release) => release.id === featuredItem.id)?.value || ''
      );
    }

    if (featuredItem.type === TracPageFeatureItemType.MerchProduct) {
      return setFeatured(
        merchList.find((merch) => merch.id === featuredItem.id)?.value || ''
      );
    }

    return setFeatured(
      links.find((link) => link.id === featuredItem.id)?.title || ''
    );
  }, [featuredItem, releases, merchList, links]);

  const featureItems = React.useMemo((): FeaturedSection[] => {
    const features: FeaturedSection[] = [];

    if (releases.length) {
      const releaseItems = !!customFeaturedFilterValue
        ? releases.filter((release) =>
            release.value?.toLowerCase().includes(customFeaturedFilterValue)
          )
        : releases;
      if (releaseItems.length) {
        features.push({ ...musicReleases, items: releaseItems });
      }
    }

    if (merchList.length) {
      const merchItems = !!customFeaturedFilterValue
        ? merchList.filter((merch) =>
            merch.value?.toLowerCase().includes(customFeaturedFilterValue)
          )
        : merchList;
      if (merchItems.length) {
        features.push({ ...merchProducts, items: merchItems });
      }
    }

    if (links.length) {
      const linkItems = links
        .filter((link) => link?.id)
        .filter((link) =>
          !!customFeaturedFilterValue
            ? link.title?.toLowerCase().includes(customFeaturedFilterValue)
            : link
        )
        .map((link) => ({
          id: link.id,
          value: link.title,
          imageUrl: link.avatar,
        }));
      if (linkItems.length) {
        features.push({ ...trackLinks, items: linkItems });
      }
    }

    return features;
  }, [releases, merchList, links, customFeaturedFilterValue]);

  const selectFeaturedItem = (
    feat: FeaturedItem,
    type: TracPageFeatureItemType
  ) => {
    if (feat?.id) {
      updateFeaturedItem({
        id: feat.id,
        type,
      });
    }
  };

  return (
    <DropdownCtrl
      title={
        <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.FEATURED.INPUT.LABEL" />
      }
      hasFilter
      filterLabel={
        <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.FEATURED.INPUT.FILTER.LABEL" />
      }
      customFilterValue={customFeaturedFilterValue}
      onCustomFilter={setCustomFeaturedFilterValue}
      value={featured}
      onClose={() => setCustomFeaturedFilterValue('')}
    >
      <DropdownWrapper onClick={(ev) => ev.stopPropagation()}>
        {featureItems.map((feats, i) => (
          <DropdownItem key={i} onClick={(ev) => ev.stopPropagation()}>
            <Typography className="title" type="small">
              {feats.label}
            </Typography>
            {feats.items.map((feat, i) => (
              <DropdownItemCheck
                onChange={() => selectFeaturedItem(feat, feats.value)}
                key={i}
                checked={feat?.id === featuredItem?.id}
              >
                {feat.value}
              </DropdownItemCheck>
            ))}
          </DropdownItem>
        ))}
      </DropdownWrapper>
    </DropdownCtrl>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(FeaturedItemContainer)``);

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DropdownItem = styled.div`
  flex: 1;
  gap: 5px;
  .title {
    color: ${({ theme }) => theme.colors.tertiary};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
